<template>
  <div class="interpretation-container">
    <div class="interpretation-section">
      <h3 class="section-title"> {{ store.modeAdmin == "IV" ? "Trough" : "Continuous" }} Concentration Interpretation</h3>
      <div v-if="!store.measuredCon">
        please enter a measured concentration !
      </div>
      <div v-else class="interpretation-content">
        <p class="interpret-item" v-html="compareToInterval"></p>
        <p class="interpret-item" v-html="compareToToxicity"></p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useTestStore } from "@/stores/testStore";

// Initialize the store
const store = useTestStore();

// Determine the type based on duration and mode of administration

const toxicityThreshold = computed(() => {
  const adminType = store.modeAdmin
  if (!store.currentAtbDataRow || !(`Conc toxicité ${adminType}` in store.currentAtbDataRow) ) {
    return null;
  }
  return {
    value: store.currentAtbDataRow[`Conc toxicité ${adminType}`],
    type: store.currentAtbDataRow[`type toxicité ${adminType}`]
  };
});

// Compute min and max based on store.modeAdmin
const minMax = computed(() => {
  const parse = v => {
    const n = parseFloat(v);
    return isNaN(n) ? undefined : n;
  };

  if (!store.currentAtbDataRow) return { min: undefined, max: undefined };

  if (store.modeAdmin === "IVSE") {
    return {
      min: parse(store.currentAtbDataRow["Cmin usuelle (Plateau_IVSE))"]),
      max: parse(store.currentAtbDataRow["Cmax usuelle (Plateau_IVSE))"]),
    };
  }

  return {
    min: parse(store.currentAtbDataRow["Cmin usuelle (Trough)"]),
    max: parse(store.currentAtbDataRow["Cmax usuelle (Trough)"]),
  };
});

const compareToInterval = computed(() => {
  if (minMax.value.min == null || minMax.value.max == null) {
    return "No data available for therapeutic dosage range.";
  }
  if (store.measuredCon < minMax.value.min) {
    return `<strong>Total concentration</strong> (${store.measuredCon}mg/L) is <strong>below</strong>  the therapeutic dosage range (${minMax.value.min} - ${minMax.value.max}mg/L).`;
  } else if (store.measuredCon > minMax.value.max) {
    return `<strong>Total concentration</strong> (${store.measuredCon}mg/L) is <strong>over</strong>  the therapeutic dosage range (${minMax.value.min} - ${minMax.value.max}mg/L).`;
  } else {
    return `<strong>Total concentration</strong> (${store.measuredCon}mg/L) is <strong>within</strong> the therapeutic dosage range (${minMax.value.min} - ${minMax.value.max}mg/L).`;
  }
});

const compareToToxicity = computed(() => {
  if (!store.currentAtbDataRow || toxicityThreshold.value.value == null) {
    return `<strong>Toxicity</strong> data not available for <strong>${store.modeAdmin}</strong> type of administration.`;
  }

  if (store.measuredCon > toxicityThreshold.value.value) {
    return `<strong>Total concentration</strong> (${store.measuredCon}mg/L) is <strong>higher</strong> than the <strong>toxicity threshold</strong> (${toxicityThreshold.value.value}mg/L)${
      toxicityThreshold.value.type
        ? `, potential <strong>${toxicityThreshold.value.type}</strong> toxicity`
        : ``
    } for <strong>${store.modeAdmin}</strong> type of administration.`;
  } else {
    return `<strong>Total concentration</strong> (${store.measuredCon}mg/L)  is <strong>below</strong> the <strong>toxicity threshold</strong> (${toxicityThreshold.value.value} mg/L) for <strong>${store.modeAdmin}</strong> type of administration.`;
  }
});
</script>

<style scoped>
.interpretation-container {
  margin-top: 20px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.interpretation-section {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  min-width: 600px;
  box-sizing: border-box;
}

.section-title {
  color: #2c3e50;
  margin: 0 0 16px 0;
  font-size: 1.25rem;
  font-weight: 600;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 8px;
}

.interpretation-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.interpret-item {
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  text-align: left;
}

.interpret-item strong {
  color: #2c3e50;
}

.status-success {
  color: #198754;
  display: block;
  padding: 8px;
  border-left: 4px solid #198754;
}

.status-warning {
  color: #ffc107;
  display: block;
  padding: 8px;
  border-left: 4px solid #ffc107;
}

.status-error {
  color: #dc3545;
  display: block;
  padding: 8px;
  border-left: 4px solid #dc3545;
}

.status-neutral {
  color: #6c757d;
  display: block;
  padding: 8px;
  border-left: 4px solid #6c757d;
}
</style> 